import { useEffect, useState } from 'react';
import {
  Table, Tag, Avatar, Button, Space, Modal, Form, Input, Select, InputNumber,
  Upload, message, Drawer, Descriptions, Popconfirm, Typography, Card, Tooltip,
  Row, Col, Divider, Tabs, DatePicker, List, Empty,
} from 'antd';
import {
  UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  UploadOutlined, PhoneOutlined, MailOutlined, IdcardOutlined,
  EnvironmentOutlined, CalendarOutlined, SafetyCertificateOutlined,
  TeamOutlined, GlobalOutlined, HomeOutlined, ContactsOutlined,
  LockOutlined, ScheduleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../store/auth';
import dayjs from 'dayjs';
import { UsersService } from '../api/services/users.service';
import { AuthService } from '../api/services/auth.service';
import { MailService } from '../api/services/mail.service';

const DEPARTMENTS = [
  'Финансовое подразделение',
  'Служба заказчика',
  'Реализация строительства',
  'Административное подразделение',
  'IT направление',
  'Коммерческое подразделение',
  'Подразделение реализации продукта',
];
const deptOptions = DEPARTMENTS.map(d => ({ value: d, label: d }));

const roleLabels = { 1: 'Сотрудник', 2: 'Начальник отдела', 3: 'Директор направления', 4: 'Генеральный директор', 5: 'Администратор', 6: 'HR-менеджер' };
const roleColors = { 1: 'blue', 2: 'cyan', 3: 'orange', 4: 'purple', 5: 'red', 6: 'geekblue' };
const roleOptions = Object.entries(roleLabels).map(([k, v]) => ({ value: Number(k), label: v }));

const specialRoleLabels = { secretary: 'Секретарь' };
const specialRoleColors = { secretary: 'magenta' };
const specialRoleOptions = Object.entries(specialRoleLabels).map(([k, v]) => ({ value: k, label: v }));

const statusLabels = { active: 'Действующий', vacation: 'Отпуск', suspended: 'Отстранён', fired: 'Уволен' };
const statusColors = { active: 'green', vacation: 'blue', suspended: 'orange', fired: 'red' };
const statusOptions = Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }));

const scheduleLabels = { standard: 'Стандартный', shift: 'Сменный', flexible: 'Гибкий' };
const scheduleOptions = Object.entries(scheduleLabels).map(([k, v]) => ({ value: k, label: v }));

const maritalOptions = [
  { value: 'single', label: 'Не в браке' },
  { value: 'married', label: 'В браке' },
  { value: 'divorced', label: 'В разводе' },
  { value: 'widowed', label: 'Вдовец/вдова' },
];

const maritalLabels = { single: 'Не в браке', married: 'В браке', divorced: 'В разводе', widowed: 'Вдовец/вдова' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [supervisorName, setSupervisorName] = useState(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [mailForm] = Form.useForm();
  const { user: currentUser } = useAuth();

  const isAdmin = (currentUser?.role_level >= 5 || currentUser?.role_level === 6 || currentUser?.username === 'khaminov');

  const fetchUsers = () => {
    setLoading(true);
    UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (!loading && users.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('userId');
      if (userId) {
        const u = users.find(user => user.id === parseInt(userId));
        if (u && !profileOpen) {
          openProfile(u);
        }
      }
    }
  }, [loading, users]);

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  const getUserName = (id) => {
    const u = users.find(u => String(u.id) === String(id));
    return u ? u.full_name : null;
  };

  const getErrorMessage = (err) => {
    const detail = err.response?.data?.detail;
    if (!detail) return 'Ошибка сервера';
    if (Array.isArray(detail)) {
      return detail.map(e => {
        const field = e.loc?.length > 1 ? e.loc[1] : 'Поле';
        return `${field}: ${e.msg || 'Ошибка валидации'}`;
      }).join(' | ');
    }
    if (typeof detail === 'string') {
      return detail;
    }
    return 'Неизвестная ошибка';
  };

  // --- Add user ---
  const handleAdd = async (values) => {
    try {
      const payload = {
        ...values,
        hire_date: values.hire_date?.format('YYYY-MM-DD'),
        birth_date: values.birth_date?.format('YYYY-MM-DD'),
      };
      await AuthService.register(payload);
      message.success('Сотрудник добавлен');
      setAddModalOpen(false);
      addForm.resetFields();
      fetchUsers();
    } catch (err) {
      message.error(getErrorMessage(err));
    }
  };

  // --- Edit user ---
  const openEdit = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      ...user,
      hire_date: user.hire_date ? dayjs(user.hire_date) : null,
      fire_date: user.fire_date ? dayjs(user.fire_date) : null,
      nda_signed_date: user.nda_signed_date ? dayjs(user.nda_signed_date) : null,
      birth_date: user.birth_date ? dayjs(user.birth_date) : null,
      password: undefined,
    });
    setEditModalOpen(true);
  };

  const handleEdit = async (values) => {
    try {
      const payload = {};
      for (const [k, v] of Object.entries(values)) {
        if (v === undefined) continue;
        if (k === 'password' && !v) continue;
        if (v && typeof v === 'object' && v.$d) {
          payload[k] = v.format('YYYY-MM-DD');
        } else {
          payload[k] = v;
        }
      }
      await UsersService.updateUser(selectedUser.id, payload);
      message.success('Сотрудник обновлён');
      setEditModalOpen(false);
      fetchUsers();
      if (profileOpen) {
        const res = await UsersService.getUserById(selectedUser.id);
        setSelectedUser(res.data);
      }
    } catch (err) {
      message.error(getErrorMessage(err));
    }
  };

  // --- Delete user ---
  const handleDelete = async (userId) => {
    try {
      await UsersService.deleteUser(userId);
      message.success('Сотрудник удалён');
      fetchUsers();
      if (profileOpen && selectedUser?.id === userId) setProfileOpen(false);
    } catch (err) {
      message.error(getErrorMessage(err));
    }
  };

  // --- Avatar upload ---
  const handleAvatarUpload = async (info, userId) => {
    const formData = new FormData();
    formData.append('file', info.file);
    try {
      const res = await UsersService.uploadAvatar(userId, formData);
      message.success('Фото обновлено');
      fetchUsers();
      if (selectedUser?.id === userId) setSelectedUser(res.data);
    } catch {
      message.error('Ошибка загрузки фото');
    }
  };

  // --- Open profile card ---
  const openProfile = (user) => {
    setSelectedUser(user);
    setProfileOpen(true);
    if (user.supervisor_id) {
      setSupervisorName(getUserName(user.supervisor_id));
    } else {
      setSupervisorName(null);
    }
  };

  const openMailConfig = async () => {
    try {
      mailForm.resetFields();
      const res = await MailService.getAdminAccountForUser(selectedUser.id);
      if (res.data) {
        mailForm.setFieldsValue({
          email: res.data.email,
          imap_host: res.data.imap_host,
          smtp_host: res.data.smtp_host,
          password: '',
        });
      } else {
        mailForm.setFieldsValue({
          imap_host: 'mail.hosting.reg.ru',
          smtp_host: 'mail.hosting.reg.ru',
        });
      }
      setMailModalOpen(true);
    } catch (e) {
      message.error('Ошибка загрузки настроек почты');
    }
  };

  const handleMailSubmit = async (values) => {
    try {
      await MailService.configureAdminAccountForUser(selectedUser.id, values);
      message.success('Почта успешно настроена');
      setMailModalOpen(false);
    } catch (e) {
      message.error('Ошибка настройки почты');
    }
  };

  // --- Parse JSON fields safely ---
  const parseJSON = (str) => {
    if (!str) return [];
    try { return JSON.parse(str); } catch { return []; }
  };

  // --- Table columns ---
  const columns = [
    {
      title: '', dataIndex: 'avatar_file_id', key: 'avatar', width: 50,
      render: (avatarId) => <Avatar src={avatarUrl(avatarId)} icon={!avatarId && <UserOutlined />} size={40} />,
    },
    {
      title: 'ФИО', dataIndex: 'full_name', key: 'name',
      render: (name, record) => <a onClick={() => openProfile(record)} style={{ fontWeight: 500 }}>{name}</a>,
    },
    { title: 'Должность', dataIndex: 'position', key: 'position' },
    { title: 'Подразделение', dataIndex: 'department', key: 'department', ellipsis: true },
    {
      title: 'Роль', dataIndex: 'role_level', key: 'role',
      render: (level) => <Tag color={roleColors[level]}>{roleLabels[level]}</Tag>,
    },
    {
      title: 'Статус', dataIndex: 'employee_status', key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>,
    },
    { title: 'Раб. телефон', dataIndex: 'phone_work', key: 'phone', responsive: ['lg'] },
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 60,
      render: (_, record) => (
        <Space>
          <Tooltip title="Редактировать в консоли Keycloak">
            <Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); window.open('/auth/admin/terra/console/#/realms/terra/users', '_blank'); }} />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  // ==================== RENDER ====================
  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Сотрудники</h1>
          <p className="page-subtitle">Справочник (данные подтягиваются из Keycloak; для админов — управление в консоли)</p>
        </div>
        {isAdmin && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => window.open('/auth/admin/terra/console/#/realms/terra/users', '_blank')}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            }}
          >
            Управление в Keycloak
          </Button>
        )}
      </div>

      <Table dataSource={users} columns={columns} rowKey="id" loading={loading}
        onRow={(record) => ({ onClick: () => openProfile(record), style: { cursor: 'pointer' } })} />

      {/* ========== ADD MODAL ========== */}
      <Modal title="Новый сотрудник" open={addModalOpen} width={640}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }}
        onOk={() => addForm.submit()} okText="Добавить">
        <Form form={addForm} layout="vertical" onFinish={handleAdd} size="middle">
          <Divider orientation="left" plain>Основное</Divider>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="position" label="Должность"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="department" label="Подразделение"><Select options={deptOptions} allowClear /></Form.Item></Col>
            <Col span={12}><Form.Item name="role_level" label="Роль в системе" initialValue={1}><Select options={roleOptions} /></Form.Item></Col>
          </Row>

          <Divider orientation="left" plain>Учётная запись</Divider>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="username" label="Логин" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="password" label="Пароль" rules={[{ required: true }]}><Input.Password /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="domain_login" label="Логин в домене"><Input /></Form.Item></Col>
          </Row>

          <Divider orientation="left" plain>Контакты</Divider>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="phone_work" label="Раб. телефон"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="phone_mobile" label="Мобильный"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="phone_internal" label="Внутренний (доб.)"><Input /></Form.Item></Col>
          </Row>

          <Divider orientation="left" plain>Даты</Divider>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="hire_date" label="Дата приёма"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="birth_date" label="Дата рождения"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="work_schedule" label="График работы"><Select options={scheduleOptions} allowClear /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* ========== EDIT MODAL ========== */}
      <Modal title={`Редактирование: ${selectedUser?.full_name}`} open={editModalOpen} width={720}
        onCancel={() => setEditModalOpen(false)} onOk={() => editForm.submit()} okText="Сохранить">
        <Form form={editForm} layout="vertical" onFinish={handleEdit} size="middle">
          <Tabs items={[
            { key: 'main', label: 'Основное', children: (
              <>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="full_name" label="ФИО"><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="position" label="Должность"><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="department" label="Подразделение"><Select options={deptOptions} allowClear /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="role_level" label="Роль"><Select options={roleOptions} /></Form.Item></Col>
                  <Col span={12}><Form.Item name="special_role" label="Специальная роль"><Select options={specialRoleOptions} allowClear placeholder="Не назначена" /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="employee_status" label="Статус"><Select options={statusOptions} /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="is_active" label="Учётная запись"><Select options={[{ value: true, label: 'Активна' }, { value: false, label: 'Заблокирована' }]} /></Form.Item></Col>
                  <Col span={12}><Form.Item name="password" label="Новый пароль"><Input.Password placeholder="Оставьте пустым" /></Form.Item></Col>
                </Row>
              </>
            )},
            { key: 'contacts', label: 'Контакты', children: (
              <>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="phone_work" label="Раб. телефон"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="phone_mobile" label="Мобильный"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="phone_internal" label="Внутренний (доб.)"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="domain_login" label="Логин в домене"><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="work_location" label="Расположение (офис, кабинет)"><Input /></Form.Item></Col>
                </Row>
                <Form.Item name="emergency_contacts" label="Контактные лица (JSON: [{name, phone, relation}])">
                  <Input.TextArea rows={3} placeholder='[{"name":"Иванов И.И.","phone":"+7...","relation":"коллега"}]' />
                </Form.Item>
              </>
            )},
            { key: 'dates', label: 'Кадры', children: (
              <>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="hire_date" label="Дата приёма"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                  <Col span={8}><Form.Item name="fire_date" label="Дата увольнения"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                  <Col span={8}><Form.Item name="work_schedule" label="График"><Select options={scheduleOptions} allowClear /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="nda_signed_date" label="Дата подписания НДА"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                  <Col span={8}><Form.Item name="birth_date" label="Дата рождения"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
              </>
            )},
            { key: 'personal', label: 'Личное', children: (
              <>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="address" label="Адрес проживания"><Input /></Form.Item></Col>
                  <Col span={6}><Form.Item name="marital_status" label="Сем. положение"><Select options={maritalOptions} allowClear /></Form.Item></Col>
                  <Col span={6}><Form.Item name="dependents_count" label="Иждивенцы"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
                <Form.Item name="bio" label="О сотруднике"><Input.TextArea rows={3} /></Form.Item>
              </>
            )},
            { key: 'skills', label: 'Квалификация', children: (
              <>
                <Form.Item name="qualification" label="Квалификация / специализация"><Input.TextArea rows={2} /></Form.Item>
                <Form.Item name="certificates" label="Сертификаты и курсы (JSON)">
                  <Input.TextArea rows={3} placeholder='["PMP","ISO 9001 Lead Auditor"]' />
                </Form.Item>
                <Form.Item name="languages" label="Языки (JSON)">
                  <Input.TextArea rows={2} placeholder='[{"lang":"Английский","level":"B2"},{"lang":"Немецкий","level":"A1"}]' />
                </Form.Item>
              </>
            )},
          ]} />
        </Form>
      </Modal>

      {/* ========== PROFILE DRAWER ========== */}
      <Drawer title={null} open={profileOpen} onClose={() => setProfileOpen(false)}
        width={560} styles={{ body: { padding: 0 } }}>
        {selectedUser && (
          <div>
            {/* ---- Header ---- */}
            <div style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              padding: '32px 24px 24px', textAlign: 'center',
            }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Avatar src={avatarUrl(selectedUser.avatar_file_id)}
                  icon={!selectedUser.avatar_file_id && <UserOutlined />}
                  size={100} style={{ border: '3px solid #fff', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }} />
                {(isAdmin || currentUser?.id === selectedUser.id) && (
                  <Upload customRequest={(info) => handleAvatarUpload(info, selectedUser.id)}
                    showUploadList={false} accept="image/*">
                    <Button shape="circle" size="small" icon={<UploadOutlined />}
                      style={{ position: 'absolute', bottom: 2, right: -4, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
                  </Upload>
                )}
              </div>
              <Typography.Title level={3} style={{ color: '#fff', margin: '12px 0 2px' }}>
                {selectedUser.full_name}
              </Typography.Title>
              <Typography.Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}>
                {selectedUser.position || 'Должность не указана'}
              </Typography.Text>
              <div style={{ marginTop: 10 }}>
                <Tag color={roleColors[selectedUser.role_level]} style={{ fontSize: 13 }}>
                  {roleLabels[selectedUser.role_level]}
                </Tag>
                {selectedUser.special_role && (
                  <Tag color={specialRoleColors[selectedUser.special_role]} style={{ fontSize: 13 }}>
                    {specialRoleLabels[selectedUser.special_role]}
                  </Tag>
                )}
                <Tag color={statusColors[selectedUser.employee_status]} style={{ fontSize: 13 }}>
                  {statusLabels[selectedUser.employee_status] || selectedUser.employee_status}
                </Tag>
              </div>
            </div>

            {/* ---- Content Tabs ---- */}
            <div style={{ padding: '0 24px 24px' }}>
              <Tabs defaultActiveKey="info" style={{ marginTop: 8 }} items={[

                // --- Вкладка: Информация ---
                { key: 'info', label: 'Информация', children: (
                  <>
                    <Card size="small" title={<><IdcardOutlined /> Основное</>} style={{ marginBottom: 12 }}>
                      <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 180, color: '#888' }}>
                        <Descriptions.Item label="ID в системе">
                          <Tag>{selectedUser.id}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Специальная роль">
                          {selectedUser.special_role ? (
                            <Tag color={specialRoleColors[selectedUser.special_role]}>
                              {specialRoleLabels[selectedUser.special_role]}
                            </Tag>
                          ) : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Подразделение">
                          {selectedUser.department || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Руководитель">
                          {selectedUser.supervisor_id ? (
                            <a onClick={() => {
                              const sup = users.find(u => String(u.id) === String(selectedUser.supervisor_id));
                              if (sup) openProfile(sup);
                            }}>
                              {getUserName(selectedUser.supervisor_id) || `ID: ${selectedUser.supervisor_id}`}
                            </a>
                          ) : '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="График работы">
                          {scheduleLabels[selectedUser.work_schedule] || selectedUser.work_schedule || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Логин в домене">
                          {selectedUser.domain_login || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Логин в системе">
                          {selectedUser.username}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={<><PhoneOutlined /> Контакты</>} style={{ marginBottom: 12 }}>
                      <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 180, color: '#888' }}>
                        <Descriptions.Item label={<><MailOutlined /> Email</>}>
                          {selectedUser.email}
                        </Descriptions.Item>
                        <Descriptions.Item label="Рабочий телефон">
                          {selectedUser.phone_work || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Мобильный">
                          {selectedUser.phone_mobile || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Внутренний (доб.)">
                          {selectedUser.phone_internal || '---'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={<><EnvironmentOutlined /> Рабочее место</>}>
                      <Typography.Text>{selectedUser.work_location || 'Не указано'}</Typography.Text>
                    </Card>
                  </>
                )},

                // --- Вкладка: Кадры ---
                { key: 'hr', label: 'Кадры', children: (
                  <>
                    <Card size="small" title={<><CalendarOutlined /> Даты</>} style={{ marginBottom: 12 }}>
                      <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 200, color: '#888' }}>
                        <Descriptions.Item label="Дата приёма">
                          {selectedUser.hire_date ? dayjs(selectedUser.hire_date).format('DD.MM.YYYY') : '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата увольнения">
                          {selectedUser.fire_date ? (
                            <Typography.Text type="danger">{dayjs(selectedUser.fire_date).format('DD.MM.YYYY')}</Typography.Text>
                          ) : '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата рождения">
                          {selectedUser.birth_date ? dayjs(selectedUser.birth_date).format('DD.MM.YYYY') : '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><LockOutlined /> НДА подписан</>}>
                          {selectedUser.nda_signed_date ? dayjs(selectedUser.nda_signed_date).format('DD.MM.YYYY') : '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="В системе с">
                          {dayjs(selectedUser.created_at).format('DD.MM.YYYY')}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={<><HomeOutlined /> Личные данные</>}>
                      <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 200, color: '#888' }}>
                        <Descriptions.Item label="Адрес проживания">
                          {selectedUser.address || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Семейное положение">
                          {maritalLabels[selectedUser.marital_status] || selectedUser.marital_status || '---'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Кол-во иждивенцев">
                          {selectedUser.dependents_count ?? '---'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </>
                )},

                // --- Вкладка: Квалификация ---
                { key: 'skills', label: 'Квалификация', children: (
                  <>
                    <Card size="small" title={<><SafetyCertificateOutlined /> Специализация</>} style={{ marginBottom: 12 }}>
                      <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {selectedUser.qualification || 'Не указана'}
                      </Typography.Paragraph>
                    </Card>

                    <Card size="small" title="Сертификаты и курсы" style={{ marginBottom: 12 }}>
                      {(() => {
                        const certs = parseJSON(selectedUser.certificates);
                        return certs.length > 0 ? (
                          <List size="small" dataSource={certs}
                            renderItem={(c) => <List.Item><SafetyCertificateOutlined style={{ marginRight: 8 }} />{c}</List.Item>} />
                        ) : <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
                      })()}
                    </Card>

                    <Card size="small" title={<><GlobalOutlined /> Языки</>}>
                      {(() => {
                        const langs = parseJSON(selectedUser.languages);
                        return langs.length > 0 ? (
                          <List size="small" dataSource={langs}
                            renderItem={(l) => (
                              <List.Item>
                                <span style={{ fontWeight: 500 }}>{l.lang}</span>
                                <Tag style={{ marginLeft: 8 }}>{l.level}</Tag>
                              </List.Item>
                            )} />
                        ) : <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
                      })()}
                    </Card>
                  </>
                )},

                // --- Вкладка: Контакты при отсутствии ---
                { key: 'emergency', label: 'Контакты', children: (
                  <Card size="small" title={<><ContactsOutlined /> Контактные лица при отсутствии</>}>
                    {(() => {
                      const contacts = parseJSON(selectedUser.emergency_contacts);
                      return contacts.length > 0 ? (
                        <List size="small" dataSource={contacts}
                          renderItem={(c) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<Avatar icon={<UserOutlined />} size="small" />}
                                title={c.name}
                                description={<>{c.phone}{c.relation ? ` (${c.relation})` : ''}</>}
                              />
                            </List.Item>
                          )} />
                      ) : <Empty description="Нет контактных лиц" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
                    })()}
                  </Card>
                )},

              ]} />

              {/* Bio */}
              {selectedUser.bio && (
                <Card size="small" title="О сотруднике" style={{ marginTop: 12 }}>
                  <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedUser.bio}</Typography.Paragraph>
                </Card>
              )}

              {/* Admin actions */}
              {isAdmin && selectedUser.id !== currentUser?.id && (
                <>
                  <Divider />
                  <Space wrap>
                    <Button icon={<EditOutlined />} onClick={() => { setProfileOpen(false); openEdit(selectedUser); }}>
                      Редактировать
                    </Button>
                    <Button icon={<MailOutlined />} onClick={openMailConfig}>
                      Настроить почту
                    </Button>
                    <Popconfirm title="Удалить сотрудника?" description="Это действие нельзя отменить"
                      onConfirm={() => handleDelete(selectedUser.id)} okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }}>
                      <Button danger icon={<DeleteOutlined />}>Удалить</Button>
                    </Popconfirm>
                  </Space>
                </>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* ========== MAIL MODAL ========== */}
      <Modal title={`Настройки почты: ${selectedUser?.full_name}`} open={mailModalOpen} 
        destroyOnClose
        onCancel={() => setMailModalOpen(false)} onOk={() => mailForm.submit()} okText="Сохранить">
        <Form form={mailForm} layout="vertical" onFinish={handleMailSubmit}>
          <Form.Item name="email" label="Email (например info@domain.ru)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="Пароль" rules={[{ required: true }]}>
            <Input.Password placeholder="Введите пароль для почты" />
          </Form.Item>
          <Form.Item name="imap_host" label="IMAP Сервер" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="smtp_host" label="SMTP Сервер" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
